import { PatientLayout } from "@/components/layout/patient-layout";
import { useGetMedication, getGetMedicationQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pill, AlertCircle, ClipboardCheck, BookOpen, Info, CheckCircle, Layers } from "lucide-react";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";

type DosageEntry = { route: string; dose: string; frequency: string; notes?: string };

export default function PatientMedicationDetail() {
  const { id } = useParams();
  const { data: medication, isLoading, error } = useGetMedication(Number(id), {
    query: { enabled: !!id, queryKey: getGetMedicationQueryKey(Number(id)) }
  });

  if (isLoading) return <PatientLayout title="Medication Guide"><LoadingScreen /></PatientLayout>;
  if (error || !medication) return <PatientLayout title="Medication Guide"><div className="p-8 text-slate-500">Medication information not found</div></PatientLayout>;

  const dosageInfo = (medication.dosageInfo ?? []) as DosageEntry[];
  const hasSideEffects = medication.sideEffects && medication.sideEffects.length > 0;
  const hasContraindications = medication.contraindications && medication.contraindications.length > 0;

  return (
    <PatientLayout title="Medication Guide">
      <Button variant="ghost" className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-6" onClick={() => window.history.back()}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <UkHealthDisclaimer variant="patient" compact />

      {/* Hero Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-8 sm:p-12 border-b border-indigo-100 flex items-center gap-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm text-indigo-500 hidden sm:block shrink-0">
            <Pill className="h-10 w-10" />
          </div>
          <div>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-1">{medication.drugClass}</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{medication.name}</h1>
            {medication.genericName && medication.genericName !== medication.name && (
              <p className="text-lg text-slate-500">Also known as: {medication.genericName}</p>
            )}
          </div>
        </div>

        {/* What it does - patient summary */}
        {(medication.patientSummary || medication.patientNotes) && (
          <div className="p-8 sm:p-12">
            <div className="flex items-start gap-4">
              <div className="bg-indigo-100 p-3 rounded-2xl shrink-0">
                <Info className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">What is it for?</h2>
                <p className="text-slate-700 leading-relaxed text-lg">
                  {medication.patientSummary ?? medication.patientNotes}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* How to take it - Patient Medication Guide */}
      {medication.patientMedicationGuide && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-teal-100 p-2.5 rounded-2xl">
              <ClipboardCheck className="h-6 w-6 text-teal-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">How to Take It</h2>
          </div>
          <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <p className="text-teal-900 leading-relaxed whitespace-pre-wrap">{medication.patientMedicationGuide}</p>
          </div>
        </div>
      )}

      {/* Dosage info (simplified for patients) */}
      {dosageInfo.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2.5 rounded-2xl">
              <BookOpen className="h-6 w-6 text-blue-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Dosage Information</h2>
          </div>
          <p className="text-slate-500 mb-5 text-sm">Your doctor will tell you your exact dose. This is for reference only.</p>
          <div className="space-y-3">
            {dosageInfo.map((d, i) => (
              <div key={i} className="border border-slate-200 rounded-2xl p-5">
                <div className="flex flex-wrap gap-6 mb-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">How it is given</p>
                    <p className="font-medium text-slate-800">{d.route}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Typical dose</p>
                    <p className="font-medium text-slate-800">{d.dose}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">How often</p>
                    <p className="font-medium text-slate-800">{d.frequency}</p>
                  </div>
                </div>
                {d.notes && (
                  <p className="text-sm text-slate-600 bg-amber-50 rounded-xl px-4 py-2 border border-amber-100">{d.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side effects (simplified) */}
      {hasSideEffects && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-2.5 rounded-2xl">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Possible Side Effects</h2>
          </div>
          <p className="text-slate-500 mb-5 text-sm">Not everyone gets these. Contact your team if any are severe or worrying.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {medication.sideEffects!.map((s, i) => (
              <div key={i} className="flex items-start gap-2 bg-amber-50 rounded-xl p-3 border border-amber-100 text-sm text-amber-900">
                <span className="text-amber-500 shrink-0 mt-0.5">•</span>
                {s}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* When NOT to take */}
      {hasContraindications && (
        <div className="bg-rose-50 rounded-3xl border border-rose-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <AlertCircle className="h-6 w-6 text-rose-600 shrink-0" />
            <h3 className="text-xl font-bold text-rose-900">When Not to Take This Medicine</h3>
          </div>
          <p className="text-rose-700 mb-4 text-sm">Tell your doctor or pharmacist if any of these apply to you:</p>
          <ul className="space-y-2">
            {medication.contraindications!.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-rose-800 text-sm">
                <span className="text-rose-500 shrink-0 mt-0.5">✗</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Why might my brand change? - Biosimilar info */}
      {medication.biosimilarInfo && medication.biosimilarInfo.biosimilars.length > 0 && (
        <div className="bg-white rounded-3xl shadow-sm border border-purple-200 p-8 mb-6" data-testid="patient-biosimilar-info">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-purple-100 p-2.5 rounded-2xl">
              <Layers className="h-6 w-6 text-purple-700" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Why might my brand change?</h2>
          </div>
          <p className="text-slate-700 leading-relaxed mb-5">
            Your pharmacy might give you a different-looking pen or injection from time to time. These are called <span className="font-semibold">biosimilars</span> — they contain the same medicine and work in the same way, but are made by different companies and may have different brand names or packaging.
          </p>
          <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5 mb-5">
            <p className="text-sm text-purple-900 leading-relaxed">{medication.biosimilarInfo.patientExplanation}</p>
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Brands you might receive in the UK:</p>
          <div className="grid sm:grid-cols-2 gap-2 mb-4">
            {medication.biosimilarInfo.biosimilars.map((b, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-3 border border-slate-200 text-sm text-slate-800">
                <span className="text-purple-500 shrink-0">•</span>
                <span className="font-medium">{b.brandName}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            If your brand has changed and you're not sure how to use the new pen, your rheumatology team or pharmacist can show you.
          </p>
        </div>
      )}

      {/* Monitoring */}
      {medication.monitoringRequirements && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-green-100 p-2.5 rounded-2xl">
              <CheckCircle className="h-6 w-6 text-green-700" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Blood Tests and Monitoring</h3>
          </div>
          <p className="text-slate-700 leading-relaxed text-sm">{medication.monitoringRequirements}</p>
        </div>
      )}

      {/* Safety reminder */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 text-white">
        <div className="flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-slate-300 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold mb-2">Important Safety Reminder</h3>
            <ul className="space-y-2 text-slate-300 text-sm">
              <li>• Always take your medication exactly as prescribed.</li>
              <li>• Do not stop taking it suddenly without speaking to your doctor.</li>
              <li>• If you feel unwell or have a high temperature, contact your rheumatology team.</li>
              <li>• Carry your medication alert card at all times if given one.</li>
            </ul>
          </div>
        </div>
      </div>
    </PatientLayout>
  );
}
